import { useAdvertisedApy, useStakingStats } from "@solomon-apps/shared";
import { SplitLayout } from "@solomon-apps/shared/src/components/console";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useClientDashboard } from "@/hooks/useClientDashboard";
import {
    useClientEarnings,
    useClientPositions
} from "@/hooks/useClientPositions";
import { usePortfolioTotals } from "@/hooks/usePortfolioTotals";
import type { DailyEarning } from "@/services/clientPositions";

import { buildHoldingDaily } from "./Dashboard/clientPortfolioMock";
import { ClientClaims } from "./Dashboard/components/ClientClaims";
import { EarningsChart } from "./Dashboard/components/EarningsChart";
import { PortfolioKeyStats } from "./Dashboard/components/PortfolioKeyStats";
import {
    ALL_POSITIONS,
    PositionFilterList
} from "./Dashboard/components/PositionFilterList";
import {
    resolveCurrentApy,
    resolveHistoricalApy,
    resolvePendingRewards
} from "./Dashboard/portfolioKeyStatsModel";
import { STATIC_BENCHMARKS } from "./Dashboard/staticBenchmarks";
import { findHolding } from "./Positions/holdings";
import { useHoldings } from "./Positions/useHoldings";
import { ClientPage } from "./shared/ClientPage";

/**
 * YaaS Client Console — Portfolio.
 *
 * Two columns: a left "Filter by Position" rail and a right earnings chart.
 * Selecting a position in the rail filters the chart to that position's own
 * cumulative series; "All Positions" (the default) shows the portfolio total.
 * The selection lives in the `?position=<uuid>` search param (absent / the
 * `all` sentinel = All Positions) so it's sharable and survives refresh.
 *
 * Charts and headline figures are served by GET /api/org/client/dashboard;
 * positions come from GET /api/org/client/positions. Benchmark reference lines
 * are static (FDIC / T-bill / Aave rates).
 */
export function DashboardPage(): React.ReactElement {
    const { data: earnings, isLoading: earningsLoading } = useClientEarnings();
    const {
        positions,
        isLoading: positionsLoading,
        error: positionsError
    } = useClientPositions();
    const { totalUsdvPositions, totalUsdvEarned, isLoadingPositions } =
        usePortfolioTotals(positions);
    // One rail row per holding — Programs are abstracted away.
    const holdings = useHoldings(positions);
    const { data: stakingStats, isLoading: statsLoading } = useStakingStats();
    const { apy, isLoading: apyLoading } = useAdvertisedApy(
        stakingStats,
        statsLoading
    );
    const {
        dashboard,
        isLoading: dashboardLoading,
        error: dashboardError
    } = useClientDashboard();

    // Selection lives in the URL so browser back / refresh / sharable links
    // work. Absent param (or the `all` sentinel) = All Positions.
    const [searchParams, setSearchParams] = useSearchParams();
    const positionParam = searchParams.get("position");
    const selected =
        positionParam && positionParam !== ALL_POSITIONS
            ? positionParam
            : ALL_POSITIONS;

    const handleSelect = (value: string): void => {
        const next = new URLSearchParams(searchParams);
        if (value === ALL_POSITIONS) {
            next.delete("position");
        } else {
            next.set("position", value);
        }
        setSearchParams(next);
    };

    // Resolve the selected holding — any of its position uuids selects it, so
    // older links keep working (falls back to All if none matches).
    const selectedHolding = useMemo(
        () =>
            selected === ALL_POSITIONS ? null : findHolding(holdings, selected),
        [holdings, selected]
    );

    // Chart series: portfolio total for All Positions, else the selected
    // holding's own cumulative series (PLACEHOLDER derivation, see mock).
    const chartDaily = useMemo<DailyEarning[]>(
        () =>
            selectedHolding
                ? buildHoldingDaily(selectedHolding.positions)
                : (earnings?.daily ?? []),
        [selectedHolding, earnings]
    );

    const isLoading = earningsLoading || positionsLoading;
    const pageTitle = "Portfolio";

    if (isLoading && !earnings) {
        return <LoadingShell title={pageTitle} />;
    }

    return (
        <ClientPage title={pageTitle}>
            {/* Shared position totals and settlement rates. */}
            <PortfolioKeyStats
                totalUsdvPositions={
                    positionsLoading ? null : totalUsdvPositions
                }
                isLoadingPositions={positionsLoading || isLoadingPositions}
                currentApy={resolveCurrentApy({
                    apy,
                    isLoading: apyLoading,
                    positions
                })}
                totalUsdvEarned={
                    positionsLoading || positionsError ? null : totalUsdvEarned
                }
                totalUsdvPending={resolvePendingRewards(positions)}
                historicalApy={resolveHistoricalApy({
                    dashboard,
                    isLoading: dashboardLoading,
                    error: dashboardError,
                    positions
                })}
                totalPositions={holdings.length}
                // ClientPage's header already draws the rule above; drop the
                // row's top border so they don't stack into a 2px line.
                topBorder={false}
            />
            <ClientClaims />
            <SplitLayout
                stackBelow="lg"
                columns={[
                    {
                        // Fixed 320px Filter rail — matches the Programs rail.
                        size: "320px",
                        className: "flex flex-col gap-6",
                        content: (
                            <PositionFilterList
                                holdings={holdings}
                                selected={
                                    selectedHolding?.primaryUuid ??
                                    ALL_POSITIONS
                                }
                                onSelect={handleSelect}
                            />
                        )
                    },
                    {
                        size: "1fr",
                        className: "flex flex-col gap-6 py-6",
                        content: (
                            <div className="px-6">
                                <EarningsChart
                                    // Re-key on the selection so the chart
                                    // remounts and re-animates its line when
                                    // the filtered series changes.
                                    key={selected}
                                    daily={chartDaily}
                                    benchmarks={STATIC_BENCHMARKS}
                                />
                            </div>
                        )
                    }
                ]}
            />
        </ClientPage>
    );
}

/* ── Ancillary UI ────────────────────────────────────────── */

function LoadingShell({
    title
}: {
    title: React.ReactNode;
}): React.ReactElement {
    return (
        <ClientPage title={title}>
            <SplitLayout
                stackBelow="lg"
                columns={[
                    {
                        // Fixed 320px Filter rail — matches the Programs rail.
                        size: "320px",
                        className: "flex flex-col gap-6",
                        content: <LoadingPositions />
                    },
                    {
                        size: "1fr",
                        className: "flex flex-col gap-6 py-6",
                        content: (
                            <div className="px-6">
                                <LoadingEarningsChart />
                            </div>
                        )
                    }
                ]}
            />
        </ClientPage>
    );
}

function LoadingPositions(): React.ReactElement {
    return (
        <section aria-label="Loading positions">
            <div className="px-6 py-4 border-b border-line-base">
                <Skeleton className="h-7 w-44 rounded-none" />
            </div>
            <div className="divide-y divide-line-base">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div
                        key={index}
                        className="px-6 py-4 flex items-center gap-3"
                    >
                        <Skeleton className="h-2 w-2 rounded-full shrink-0" />
                        <div className="flex flex-col gap-1.5">
                            <Skeleton className="h-4 w-28 rounded-none" />
                            <Skeleton className="h-3 w-20 rounded-none" />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function LoadingEarningsChart(): React.ReactElement {
    return (
        <Card
            asElement="section"
            border={false}
            rounded={false}
            aria-label="Loading earnings over time"
        >
            <div className="flex flex-col gap-4 p-4 border-b border-line-base">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-3">
                        <Skeleton className="h-4 w-32 rounded-none" />
                        <Skeleton className="h-7 w-56 rounded-none" />
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="h-10 w-16 rounded-none" />
                        <Skeleton className="h-10 w-14 rounded-none" />
                    </div>
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Skeleton className="h-4 w-20 rounded-none" />
                        <Skeleton className="h-10 w-32 rounded-none" />
                        <Skeleton className="h-10 w-28 rounded-none" />
                        <Skeleton className="h-10 w-28 rounded-none" />
                    </div>
                    <div className="flex items-center gap-2">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <Skeleton
                                key={index}
                                className="h-9 w-10 rounded-none"
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div className="px-2 pt-4 pb-2">
                <div className="relative h-[300px] px-10 py-6">
                    {Array.from({ length: 5 }).map((_, index) => (
                        <div
                            key={index}
                            className="absolute left-10 right-4 h-px bg-line-base"
                            style={{ top: `${12 + index * 20}%` }}
                        />
                    ))}
                    <Skeleton className="absolute left-0 top-6 h-4 w-10 rounded-none" />
                    <Skeleton className="absolute left-0 top-[33%] h-4 w-10 rounded-none" />
                    <Skeleton className="absolute left-0 top-[58%] h-4 w-10 rounded-none" />
                    <Skeleton className="absolute left-0 bottom-9 h-4 w-10 rounded-none" />
                    <Skeleton className="absolute left-[13%] bottom-2 h-4 w-20 rounded-none" />
                    <Skeleton className="absolute left-[34%] bottom-2 h-4 w-20 rounded-none" />
                    <Skeleton className="absolute left-[55%] bottom-2 h-4 w-20 rounded-none" />
                    <Skeleton className="absolute right-4 bottom-2 h-4 w-20 rounded-none" />
                    <Skeleton className="absolute left-[18%] top-[62%] h-2 w-[34%] rounded-none" />
                    <Skeleton className="absolute right-[8%] top-[20%] h-2 w-[22%] rounded-none" />
                </div>
            </div>
            <div className="px-4 py-3 border-t border-line-base flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-6">
                    <Skeleton className="h-4 w-28 rounded-none" />
                    <Skeleton className="h-4 w-36 rounded-none" />
                </div>
                <Skeleton className="h-4 w-72 rounded-none" />
            </div>
        </Card>
    );
}
